"use client";

import { timelineEvents } from "@/data/timelineEvents";
import React, { useState } from "react";
import FlyingBees from "./FlyingBees";

export default function TimelineSection() {
    const [showPending, setShowPending] = useState(false);

    const completedEvents = timelineEvents.filter((event) => !event.isPending);
    const pendingEvents = timelineEvents.filter((event) => event.isPending);
    const displayedEvents = showPending ? timelineEvents : completedEvents;

    return (
        <section className="timeline-section relative py-16 px-6" style={{ backgroundColor: "#fefdf8" }}>
            <div className="max-w-6xl mx-auto relative z-10">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Projektübersicht</h2>
                    <p className="text-base md:text-lg text-amber-600 font-medium max-w-3xl mx-auto">
                        BeeHappy verbindet Natur, Wissen und Technologie: Ein Bienenstock voller Leben, dessen Daten wir
                        dank Sensorik erlebbar machen.
                    </p>
                </div>

                {/* Timeline Grid */}
                <div className="relative">
                    {/* Connecting Line - Mobile: center, Desktop: center */}
                    <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-200 via-amber-300 to-amber-200 transform -translate-x-1/2" />

                    {/* Timeline Events */}
                    <div className="space-y-8 md:space-y-12 relative">
                        {displayedEvents.map((event, index) => (
                            <div key={`${event.date}-${event.title}`}>
                                {/* Mobile Layout */}
                                <div className="md:hidden">
                                    {/* Date Circle - Centered */}
                                    <div className="flex justify-center mb-4">
                                        <div
                                            className={`inline-block px-4 py-2 rounded-full text-sm font-bold shadow-sm transition-all duration-300 ${
                                                event.isPending
                                                    ? "bg-white text-gray-600 border-2 border-dashed border-gray-400"
                                                    : "bg-white text-amber-800 border-2 border-amber-200"
                                            }`}
                                        >
                                            {event.date}
                                        </div>
                                    </div>

                                    {/* Timeline Card - Full width */}
                                    <div className="mx-4 transform hover:scale-[1.02] transition-all duration-300">
                                        <div
                                            className={`rounded-xl p-6 relative overflow-hidden group ${
                                                event.isPending
                                                    ? "bg-white border-2 border-dashed border-gray-300 shadow-sm hover:shadow-md"
                                                    : "bg-white shadow-md hover:shadow-lg border border-gray-100"
                                            }`}
                                        >
                                            {/* Subtle Honeycomb Background Pattern */}
                                            <div className="absolute inset-0 opacity-2">
                                                <div
                                                    className="absolute inset-0"
                                                    style={{
                                                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolygon points='40,10 70,25 70,55 40,70 10,55 10,25' fill='%23${event.isPending ? "6b7280" : "f59e0b"}' fill-opacity='0.03'/%3E%3C/svg%3E")`,
                                                        backgroundSize: "40px 40px",
                                                    }}
                                                />
                                            </div>

                                            {/* Content */}
                                            <div className="relative z-10">
                                                <div className="flex items-start mb-4">
                                                    <div
                                                        className={`text-3xl mr-4 p-2 rounded-full transition-all duration-300 ${
                                                            event.isPending
                                                                ? "bg-gray-100 opacity-60"
                                                                : "bg-amber-50 group-hover:bg-amber-100"
                                                        }`}
                                                    >
                                                        {event.icon}
                                                    </div>
                                                    <div className="flex-1">
                                                        <h3
                                                            className={`text-xl font-bold mb-2 ${
                                                                event.isPending ? "text-gray-600" : "text-gray-900"
                                                            }`}
                                                        >
                                                            {event.title}
                                                        </h3>
                                                        {!event.isPending && (
                                                            <div className="w-12 h-1 bg-amber-400 rounded-full mb-3" />
                                                        )}
                                                    </div>
                                                </div>

                                                <p
                                                    className={`text-sm leading-relaxed ${
                                                        event.isPending ? "text-gray-500" : "text-gray-700"
                                                    }`}
                                                >
                                                    {event.description}
                                                </p>
                                            </div>

                                            {/* Hover Effect Border */}
                                            <div
                                                className={`absolute inset-0 rounded-xl border-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                                                    event.isPending ? "border-gray-300" : "border-amber-200"
                                                }`}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Desktop Layout */}
                                <div
                                    className={`hidden md:flex items-center ${
                                        index % 2 === 0 ? "flex-row" : "flex-row-reverse"
                                    }`}
                                >
                                    {/* Date Circle */}
                                    <div className="w-40 flex-shrink-0 text-center">
                                        <div
                                            className={`inline-block px-6 py-3 rounded-full text-sm font-bold shadow-sm transition-all duration-300 ${
                                                event.isPending
                                                    ? "bg-white text-gray-600 border-2 border-dashed border-gray-400"
                                                    : "bg-white text-amber-800 border-2 border-amber-200"
                                            }`}
                                        >
                                            {event.date}
                                        </div>
                                    </div>

                                    {/* Connecting Line to Circle */}
                                    <div className={`w-20 h-1 ${event.isPending ? "bg-gray-300" : "bg-amber-200"}`} />

                                    {/* Timeline Card */}
                                    <div
                                        className={`flex-1 ${
                                            index % 2 === 0 ? "ml-6" : "mr-6"
                                        } transform hover:scale-[1.02] transition-all duration-300 hover:-translate-y-1`}
                                    >
                                        <div
                                            className={`rounded-xl p-8 relative overflow-hidden group ${
                                                event.isPending
                                                    ? "bg-white border-2 border-dashed border-gray-300 shadow-sm hover:shadow-md"
                                                    : "bg-white shadow-md hover:shadow-lg border border-gray-100"
                                            }`}
                                        >
                                            {/* Subtle Honeycomb Background Pattern */}
                                            <div className="absolute inset-0 opacity-2">
                                                <div
                                                    className="absolute inset-0"
                                                    style={{
                                                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolygon points='40,10 70,25 70,55 40,70 10,55 10,25' fill='%23${event.isPending ? "6b7280" : "f59e0b"}' fill-opacity='0.03'/%3E%3C/svg%3E")`,
                                                        backgroundSize: "40px 40px",
                                                    }}
                                                />
                                            </div>

                                            {/* Content */}
                                            <div className="relative z-10">
                                                <div className="flex items-start mb-6">
                                                    <div
                                                        className={`text-4xl mr-4 p-3 rounded-full transition-all duration-300 ${
                                                            event.isPending
                                                                ? "bg-gray-100 opacity-60"
                                                                : "bg-amber-50 group-hover:bg-amber-100"
                                                        }`}
                                                    >
                                                        {event.icon}
                                                    </div>
                                                    <div className="flex-1">
                                                        <h3
                                                            className={`text-2xl font-bold mb-2 ${
                                                                event.isPending ? "text-gray-600" : "text-gray-900"
                                                            }`}
                                                        >
                                                            {event.title}
                                                        </h3>
                                                        {!event.isPending && (
                                                            <div className="w-16 h-1 bg-amber-400 rounded-full mb-4" />
                                                        )}
                                                    </div>
                                                </div>

                                                <p
                                                    className={`text-base leading-relaxed ${
                                                        event.isPending ? "text-gray-500" : "text-gray-700"
                                                    }`}
                                                >
                                                    {event.description}
                                                </p>
                                            </div>

                                            {/* Hover Effect Border */}
                                            <div
                                                className={`absolute inset-0 rounded-xl border-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                                                    event.isPending ? "border-gray-300" : "border-amber-200"
                                                }`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Show More/Less Button */}
                    {!showPending && pendingEvents.length > 0 && (
                        <div className="flex justify-center mt-12 md:mt-16">
                            <button
                                type="button"
                                onClick={() => setShowPending(true)}
                                className="group relative bg-white hover:bg-amber-50 border-2 border-amber-200 hover:border-amber-300 rounded-xl px-6 py-4 md:px-8 md:py-6 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer transform hover:scale-105"
                            >
                                <div className="flex flex-col items-center">
                                    <div className="mb-2 md:mb-3">
                                        <svg
                                            className="w-5 h-5 md:w-6 md:h-6 text-amber-600 group-hover:text-amber-700 transition-colors"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 9l-7 7-7-7"
                                            />
                                        </svg>
                                    </div>
                                    <span className="text-amber-700 font-semibold text-sm md:text-base">
                                        Geplante Meilensteine anzeigen
                                    </span>
                                    <span className="text-amber-600 text-xs md:text-sm mt-1">
                                        +{pendingEvents.length} weitere Ereignisse
                                    </span>
                                </div>
                            </button>
                        </div>
                    )}

                    {showPending && (
                        <div className="flex justify-center mt-12 md:mt-16">
                            <button
                                type="button"
                                onClick={() => setShowPending(false)}
                                className="group relative bg-white hover:bg-gray-50 border-2 border-gray-300 hover:border-gray-400 rounded-xl px-6 py-3 md:px-8 md:py-4 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer transform hover:scale-105"
                            >
                                <div className="flex items-center">
                                    <svg
                                        className="w-4 h-4 md:w-5 md:h-5 text-gray-600 group-hover:text-gray-700 transition-colors mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 15l7-7 7 7"
                                        />
                                    </svg>
                                    <span className="text-gray-700 font-semibold text-sm md:text-base">
                                        Geplante Meilensteine ausblenden
                                    </span>
                                </div>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <FlyingBees
                bees={[
                    { top: "20%", left: "10%", width: 80 },
                    { top: "40%", left: "88%", width: 85 },
                    { top: "65%", left: "12%", width: 75 },
                    { top: "85%", left: "85%", width: 90 },
                ]}
            />
        </section>
    );
}
